import React, { useState } from "react"
import { TextField, Button, Alert, CircularProgress, IconButton } from "@mui/material"
import { X } from "lucide-mui"
import { Formik } from "formik"
import * as Yup from "yup"
import i18n from "@/i18n"
import { useMutation } from "@apollo/client/react"
import {
  UpdatePasswordDocument,
  type UpdatePasswordMutation,
  type UpdatePasswordMutationVariables,
} from "@/graphql/graphql"

const validationSchema = Yup.object({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .required("New password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[0-9]/, "Password must contain at least one number"),
  confirmPassword: Yup.string()
    .required("Password confirmation is required")
    .oneOf([Yup.ref("newPassword")], "Passwords must match"),
})

const ChangePassword: React.FC = () => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [updatePasswordMutation, { loading: saving }] = useMutation<
    UpdatePasswordMutation,
    UpdatePasswordMutationVariables
  >(UpdatePasswordDocument)

  const handleSubmit = async (values, { resetForm }) => {
    setSuccessMessage(null)
    setErrorMessage(null)

    try {
      const result = await updatePasswordMutation({
        variables: {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
          newPasswordConfirmation: values.confirmPassword,
        },
      })

      if (result.data?.updatePassword?.success) {
        resetForm()
        setSuccessMessage(i18n.t("app.settings.profile.change_password.success"))
      } else {
        const errors = result.data?.updatePassword?.errors
        const errorCode = errors?.[0]?.code
        const errorKey = errorCode
          ? `app.settings.profile.change_password.errors.${errorCode.toLowerCase()}`
          : "app.settings.profile.change_password.errors.unknown"
        setErrorMessage(i18n.t(errorKey))
      }
    } catch (err) {
      console.error("Error updating password:", err)
      setErrorMessage(i18n.t("app.settings.profile.change_password.errors.generic"))
    }
  }

  return (
    <Formik
      initialValues={{
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <form
          onSubmit={formik.handleSubmit}
          className="flex flex-col gap-6 max-w-4xl"
          data-testid="settings-password-form"
        >
          <h1 className="text-3xl font-bold mb-0" data-testid="settings-password-title">
            {i18n.t("app.settings.profile.change_password.title")}
          </h1>

          {successMessage && (
            <Alert
              severity="success"
              data-testid="settings-password-alert-success"
              action={
                <IconButton aria-label="close" color="inherit" size="small" onClick={() => setSuccessMessage(null)}>
                  <X className="w-5 h-5" />
                </IconButton>
              }
            >
              {successMessage}
            </Alert>
          )}

          {errorMessage && (
            <Alert
              severity="error"
              data-testid="settings-password-alert-error"
              action={
                <IconButton aria-label="close" color="inherit" size="small" onClick={() => setErrorMessage(null)}>
                  <X className="w-5 h-5" />
                </IconButton>
              }
            >
              {errorMessage}
            </Alert>
          )}

          <div className="bg-white rounded-lg shadow p-6" data-testid="settings-password-section">
            <Alert severity="info" className="mb-6" data-testid="settings-password-requirements">
              {i18n.t("app.settings.profile.change_password.requirements")}
            </Alert>

            <div className="mb-4">
              <TextField
                label={i18n.t("app.settings.profile.change_password.current_password")}
                name="currentPassword"
                type="password"
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.currentPassword && Boolean(formik.errors.currentPassword)}
                helperText={(formik.touched.currentPassword && formik.errors.currentPassword) as string}
                fullWidth
                required
                data-testid="settings-password-input-current"
              />
            </div>

            <div className="mb-4">
              <TextField
                label={i18n.t("app.settings.profile.change_password.new_password")}
                name="newPassword"
                type="password"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                helperText={(formik.touched.newPassword && formik.errors.newPassword) as string}
                fullWidth
                required
                data-testid="settings-password-input-new"
              />
            </div>

            <div className="mb-4">
              <TextField
                label={i18n.t("app.settings.profile.change_password.confirm_password")}
                name="confirmPassword"
                type="password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={(formik.touched.confirmPassword && formik.errors.confirmPassword) as string}
                fullWidth
                required
                data-testid="settings-password-input-confirm"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outlined"
              color="primary"
              onClick={() => formik.resetForm()}
              disabled={saving}
              data-testid="settings-password-btn-cancel"
            >
              {i18n.t("app.actions.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={saving || !formik.dirty || !formik.isValid}
              data-testid="settings-password-btn-submit"
            >
              {saving ? <CircularProgress size={24} /> : i18n.t("app.settings.profile.change_password.update_password")}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  )
}

export default ChangePassword
